import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Award, Shield, CheckCircle, Clock, AlertCircle, Lock } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  preferred_name?: string;
  email: string;
  personal_email?: string;
  phone?: string;
  employment_type: string;
  employment_status: string;
  department_name?: string;
  title: string;
  manager_name?: string;
  hire_date: string;
  location?: string;
  remote: boolean;
  emergency_contacts: Array<{ name: string; relationship: string; phone: string }>;
  certifications: Array<{ name: string; issuer: string; expiry_date?: string; status: string }>;
  skills: string[];
  compensation?: { salary: number; currency: string; pay_frequency: string };
}

interface PolicyAck {
  policy_id: string;
  title: string;
  status: 'signed' | 'pending' | 'overdue';
  signed_at: string | null;
}

export function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'info' | 'policies'>('info');
  const { data: employee, loading } = useApi<Employee>(`employees/${id}`);
  const { data: policiesData } = useApi<{ policies: PolicyAck[] }>(`legal/policies/employee-status?employee_id=${id ?? ''}`, [id]);

  if (loading) return <div className="text-muted-foreground">Loading...</div>;
  if (!employee) return <div className="text-muted-foreground">Employee not found</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/employees')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to Employees
        </button>
        <Link to={`/employees/${id}/sensitive-info`} className="flex items-center gap-2 text-sm px-3 py-1.5 border border-amber-500/30 text-amber-600 rounded-md hover:bg-amber-500/10">
          <Lock className="w-3.5 h-3.5" /> Sensitive Info
        </Link>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border mb-2">
        <button
          onClick={() => setActiveTab('info')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'info' ? 'border-amber-500 text-amber-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Profile
        </button>
        <button
          onClick={() => setActiveTab('policies')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'policies' ? 'border-amber-500 text-amber-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <Shield className="w-3.5 h-3.5" />
          Policies
          {policiesData && policiesData.policies.filter(p => p.status !== 'signed').length > 0 && (
            <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-red-500/10 text-red-400">
              {policiesData.policies.filter(p => p.status !== 'signed').length}
            </span>
          )}
        </button>
      </div>

      {/* Policies Tab */}
      {activeTab === 'policies' && (
        <div className="space-y-4">
          {!policiesData || policiesData.policies.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No policies assigned.</p>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="divide-y divide-border">
                {policiesData.policies.map(p => (
                  <div key={p.policy_id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {p.status === 'signed' && <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />}
                      {p.status === 'pending' && <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                      {p.status === 'overdue' && <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />}
                      <span className="text-sm text-foreground truncate">{p.title}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {p.status === 'signed' && p.signed_at && (
                        <span className="text-xs text-muted-foreground">{new Date(p.signed_at).toLocaleDateString()}</span>
                      )}
                      {p.status !== 'signed' && (
                        <Link
                          to={`/policies/${p.policy_id}/sign`}
                          className="text-xs text-amber-500 hover:text-amber-400 font-medium"
                        >
                          Sign now
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'info' && <>

      {/* Header */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-xl font-bold">
              {employee.first_name[0]}{employee.last_name[0]}
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                {employee.first_name} {employee.last_name}
                {employee.preferred_name && <span className="text-muted-foreground font-normal"> ({employee.preferred_name})</span>}
              </h2>
              <p className="text-sm text-muted-foreground">{employee.title} &middot; {employee.department_name ?? 'No Department'}</p>
              <div className="flex items-center gap-3 mt-2">
                <StatusBadge status={employee.employment_status} />
                <span className="text-xs text-muted-foreground capitalize">{employee.employment_type.replace('_', ' ')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Contact Info */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Contact Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-4 h-4" /> {employee.email}</div>
            {employee.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-4 h-4" /> {employee.phone}</div>}
            {employee.location && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-4 h-4" /> {employee.location} {employee.remote && '(Remote)'}</div>}
            <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-4 h-4" /> Hired {employee.hire_date?.split('T')[0]}</div>
            {employee.manager_name && <div className="text-muted-foreground">Manager: {employee.manager_name}</div>}
          </div>
        </Card>

        {/* Skills */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Skills</h3>
          <div className="flex flex-wrap gap-2">
            {employee.skills.length === 0 ? (
              <p className="text-xs text-muted-foreground">No skills listed</p>
            ) : (
              employee.skills.map(skill => (
                <span key={skill} className="px-2 py-1 bg-surface-3 rounded-md text-xs text-foreground">{skill}</span>
              ))
            )}
          </div>
        </Card>

        {/* Certifications */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> Certifications</h3>
          {employee.certifications.length === 0 ? (
            <p className="text-xs text-muted-foreground">No certifications</p>
          ) : (
            <div className="space-y-2">
              {employee.certifications.map(cert => (
                <div key={cert.name} className="flex items-center justify-between py-1">
                  <div>
                    <p className="text-sm text-foreground">{cert.name}</p>
                    <p className="text-xs text-muted-foreground">{cert.issuer}</p>
                  </div>
                  <StatusBadge status={cert.status} />
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Emergency Contacts */}
        <Card>
          <h3 className="text-sm font-medium text-foreground mb-3">Emergency Contacts</h3>
          {employee.emergency_contacts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No emergency contacts</p>
          ) : (
            <div className="space-y-2">
              {employee.emergency_contacts.map((c, i) => (
                <div key={i} className="py-1">
                  <p className="text-sm text-foreground">{c.name} <span className="text-muted-foreground">({c.relationship})</span></p>
                  <p className="text-xs text-muted-foreground">{c.phone}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      </>}
    </div>
  );
}
